import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { bellCountQuery, bellPanelQuery, markAllRead, markRead, type BellRow } from '../api/notifications'
import { fromNow, stamp } from '../inventoree/format'
import { placeUrl } from '../dashboard/cardLinks'
import { navUrl } from './Shell'
import './NotificationsBell.css'

/**
 * CLOPOTELUL DIN ANTET.
 *
 * -----------------------------------------------------------------------------
 * CE ERA AICI
 * -----------------------------------------------------------------------------
 *
 * Markupul clopotelului fusese copiat in `Shell.tsx` din `_layout/header.php`
 * ca sa arate la fel - dar cu `onClick={(e) => e.preventDefault()}` si cu
 * insigna scrisa de mana, `hidden`, cu un zero in ea. Adica un desen. Nu exista
 * niciun capat in spatele lui, deci acelasi om care avea 17 instiintari in
 * portal nu vedea aici niciun numar si nu deschidea nimic.
 *
 * In portal purtarea nu statea in macheta, ci in `public/js/general.js`
 * (`UserNotifications`) - iar v4 incarca FOILE portalului, nu si scripturile
 * lui. Orice s-ar fi sprijinit pe jQuery sau pe `data-toggle` era dinainte
 * mort; aici nu se sprijina pe nimic din ele.
 *
 * -----------------------------------------------------------------------------
 * PANOUL, NU SERTARUL DIN DREAPTA
 * -----------------------------------------------------------------------------
 *
 * Portalul deschidea sertarul din dreapta (`.quick-sidebar-navigation` din
 * subsolul paginii). Sertarul acela nu s-a mutat inca in v4 - `SIDEBAR_READY`
 * din `Shell.tsx` e chiar comutatorul lui - deci lista sta intr-un panou al
 * clopotelului, ca sertarul rotitei si ca meniul de firma, care sunt deja aici.
 * Foile portalului n-au reguli pentru forma asta (cele din `.dropdown-notifi`
 * sunt scrise pentru fundalul inchis al sertarului si ar fi dat scris alb pe
 * alb), deci desenul e in `NotificationsBell.css`, langa componenta.
 */

/**
 * ADRESELE CU NUMAR, care in `MOVED` nu au cum sa stea.
 *
 * `navUrl()` cauta o potrivire EXACTA in tabelul paginilor mutate, iar acolo
 * sunt scrise intrarile de meniu - `easeedesk/tickets`, nu `easeedesk/ticket/
 * 4212`. Felurile de instiintare insa tocmai asemenea adrese scriu: catalogul
 * tine `url` (`easeedesk/ticket/`) si randul tine coada (`4212`).
 *
 * Fara randul de mai jos fiecare instiintare de tichet ar fi plecat la portalul
 * vechi - care raspunde cu 302 inapoi incoace, deci fila noua s-ar fi incarcat
 * de doua ori, sau, pe o instalare fara portal, deloc.
 */
const WITH_ID: Record<string, string> = {
  'easeedesk/ticket': '/app/easeedesk/ticket',
  /* Asignarea aparatelor din AD (`DeviceAssignment`): fisa aparatului din v4. */
  'inventoree/device': '/app/inventoree/device',
  /* Expirarile aplicatiilor de business (`business_app_expiry`): fisa aplicatiei. */
  applications: '/app/applications',
}

/** Adresa pe care o poarta un rand: cea din v4 daca ecranul s-a mutat, cea a portalului altfel. */
export function bellHref(portalPath: string): string {
  const clean = portalPath.replace(/^\/+/, '').replace(/\/+$/, '')
  if (clean === '') return ''

  const cut = clean.lastIndexOf('/')
  if (cut > 0) {
    const base = clean.slice(0, cut)
    const tail = clean.slice(cut + 1)
    const moved = WITH_ID[base]
    if (moved !== undefined && /^\d+$/.test(tail)) return `${moved}/${tail}`
  }

  /*
   * `navUrl()` stie intrarile de MENIU, pe potrivire intreaga. Alarmele din
   * clopotel poarta insa adresele CARDURILOR de tablou (`Advice::read`), si
   * pentru ele raspunsul e `hrefOf()`, aceeasi mapare ca sub carduri: fara ea,
   * `protectee/backup_summary` si `discovery` ieseau din /app (18 septembrie
   * 2026), iar orice adresa de card cu `?` dupa ea ar fi iesit la fel. Ce stie
   * deja `navUrl()` sub /app ramane neatins, iar o adresa moarta (pe care
   * `placeUrl` o lasa cum a venit, fara bara) ramane la raspunsul lui.
   */
  const menu = navUrl(clean)
  if (menu.startsWith('/app/')) return menu

  const placed = placeUrl(clean)

  return placed.startsWith('/') ? placed : menu
}

/** Culoarea pastilei, ca `_severity` din `general.js`. */
const TONE: Record<string, string> = {
  info: 'bg-blue',
  success: 'bg-theme-inverse',
  warning: 'bg-warning',
  error: 'bg-theme',
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLLIElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const badge = useQuery(bellCountQuery)
  const panel = useQuery({ ...bellPanelQuery, enabled: open })

  useEffect(() => {
    if (!open) return
    const away = (event: globalThis.MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('click', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  /*
   * Dupa o scriere: insigna primeste PE LOC numarul intors de server - el e cel
   * adevarat, si l-am cerut oricum - iar lista se reciteste. Panoul e deschis
   * in clipa aia, deci recitirea chiar se intampla; inchis, `enabled: false` o
   * lasa pentru urmatoarea deschidere, cand oricum ar fi fost nevoie de ea.
   */
  const refreshBadge = (count: number) => {
    queryClient.setQueryData(bellCountQuery.queryKey, { count })
    void queryClient.invalidateQueries({ queryKey: bellPanelQuery.queryKey })
  }

  const dismiss = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: (data) => refreshBadge(data.count),
  })

  const clearAll = useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: (data) => refreshBadge(data.count),
  })

  const rows = panel.data?.notifications ?? []
  const count = badge.data?.count ?? 0
  const now = new Date()

  const follow = (row: BellRow) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (row.href === null) return
    /* Ctrl-clic, rotita, "deschide in fila noua": lasam browserul in pace. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()

    /*
     * STERGEREA PLEACA INAINTEA MUTARII, nu dupa.
     *
     * In portal `hide(100, () => $.post(...))` atarna de sfarsitul animatiei,
     * iar `window.open()` de dedesubt trecea fila in fundal - unde browserul nu
     * mai cheama `requestAnimationFrame`, deci animatia nu se termina si
     * stergerea nu mai pleca niciodata. Aici nu exista nimic intre apasare si
     * cerere.
     */
    if (row.closeOnClick && row.dismissible) dismiss.mutate(row.id)

    const to = bellHref(row.href)
    setOpen(false)
    if (to.startsWith('/app/')) navigate({ to: to.slice('/app'.length) })
    else window.location.assign(to)
  }

  /*
   * Aceleasi clase ca in `_layout/header.php`: `hidden-md hidden-sm`, si NU
   * `hidden-xs` - de pe 6 septembrie 2026 clopotelul poarta si alarmele
   * tabloului, iar ascuns pe telefon ele n-ar fi ajuns niciodata la omul care
   * nu sta in fata tabloului, adica tocmai la cel care are nevoie de ele.
   * `od-nav-bell` e numele dupa care il alege foaia barii de titlu de pe telefon.
   */
  return (
    <li className={`nav-quick-actions od-nav-bell hidden-md hidden-sm${open ? ' open' : ''}`} ref={box}>
      <a
        id="my_notifications_header"
        className="dropdown-toggle my_notifications"
        title="Notifications"
        href="#"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        onClick={(e) => {
          e.preventDefault()
          setOpen((v) => !v)
        }}
      >
        <i className="flaticon-alert-2 flaticon-20" aria-hidden="true"></i>
        {/* Insigna se ascunde la zero, ca in portal - acolo prin clasa `hidden`. */}
        <span id="notifications-badge-header" className={`label bg-warning font-10${count > 0 ? '' : ' hidden'}`}>
          {count}
        </span>
      </a>

      <div className="od-bell-panel" role="dialog" aria-label="Notifications" hidden={!open}>
        <div className="od-bell-head">
          <span className="od-bell-title">
            <i className="flaticon-alert-2" aria-hidden="true"></i> Notifications
          </span>
          {rows.some((row) => row.dismissible) && (
            <button
              type="button"
              id="clear-notifications"
              className="od-bell-clear"
              disabled={clearAll.isPending}
              onClick={() => clearAll.mutate()}
            >
              Clear all
            </button>
          )}
        </div>

        <div className="od-bell-body">
          {panel.isPending && <p className="od-bell-quiet">Reading…</p>}

          {panel.isError && (
            <p className="od-bell-quiet od-bell-broken">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> The notifications could not be read.
            </p>
          )}

          {!panel.isPending && !panel.isError && rows.length === 0 && (
            <p className="od-bell-quiet">
              <i className="far fa-bell-slash" aria-hidden="true"></i> You don&apos;t have notifications
            </p>
          )}

          <ul className="od-bell-list">
            {rows.map((row) => {
              const when = stamp(row.created)
              const to = row.href === null ? null : bellHref(row.href)

              return (
                <li key={row.id} className={`od-bell-row od-bell-${row.kind}`} data-id={row.id}>
                  <a
                    className={to === null ? 'od-bell-line od-bell-flat' : 'od-bell-line'}
                    href={to ?? undefined}
                    onClick={to === null ? (e) => e.preventDefault() : follow(row)}
                  >
                    <span className={`od-bell-mark ${TONE[row.severity] ?? TONE.info}`}>
                      <i className={row.icon} aria-hidden="true"></i>
                    </span>
                    <span className="od-bell-what">
                      <span className="od-bell-name">{row.title}</span>
                      <span className="od-bell-text">{row.text}</span>
                      {when !== null && <time className="od-bell-when">{fromNow(when, now)}</time>}
                    </span>
                  </a>
                  {/*
                    Crucea sta NUMAI pe instiintari. O alarma e o stare care se
                    stinge cand cifra intra sub prag; inchisa cu mana, s-ar
                    intoarce la urmatoarea deschidere si butonul ar parea stricat.
                  */}
                  {row.dismissible && (
                    <button
                      type="button"
                      className="od-bell-x"
                      title="Mark as read"
                      aria-label="Mark as read"
                      onClick={() => dismiss.mutate(row.id)}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </li>
  )
}
